import { makeEnum, makeOptions,
  setupClass, lookupData, 
  interpolateInMap,
} from '../Base.js'
export { Units } from '../Common/Units.js'

import {
  FieldType,
  Field,
} from '../Common/Field.js'

import {
  YesNo,
  } from '../Components/Common.js'
import { InputComponent } from '../Common/InputComponent.js'
import { CalcContext } from '../Common/CalcContext.js'

export let HouseInternalGainsMethod = makeEnum({
  // Note: use 'Yes' and 'No' as keys for backwards compatibility.
  // This input used to be a plain Yes/No input.
  Yes: 'Automatic',
  No: 'Manual',
})

export class HouseInternalsData extends InputComponent {
  init(proj) {
    this.proj = proj;

    this.useStdMethod = new Field({
      //name: 'Use standard internal gain calculation?',
      name: 'Entry method',
      type: FieldType.Select,
      choices: makeOptions(HouseInternalGainsMethod),
      bold: true,
    })
    // For YES:
    this.additionalOccupants = new Field({
      name: 'Number occupants',
      type: FieldType.Count,
      defaultValue: 3,
    })
    this.additionalOccupants.setVisibility(() => {
      return this.useStdMethod.value == YesNo.No;
    })
    this.additionalSensibleLoads = new Field({
      name: 'Additional sensible loads',
      type: FieldType.Load,
      defaultValue: 100,
      metricDefaultValue: 30,
    })
    this.additionalSensibleLoads.setVisibility(() => {
      return this.useStdMethod.value == YesNo.No;
    })
    this.additionalLatentLoads = new Field({
      name: 'Additional latent loads',
      type: FieldType.Load,
      defaultValue: 50,
      metricDefaultValue: 15,
    })
    this.additionalLatentLoads.setVisibility(() => {
      return this.useStdMethod.value == YesNo.No;
    })

    this.outputSensibleLoad = new Field({
      name: 'Total sensible gains',
      type: FieldType.Load,
      isOutput: true,
    })
    this.outputLatentLoad = new Field({
      name: 'Total latent gains',
      type: FieldType.Load,
      isOutput: true,
    })
    this.outputNumPeople = new Field({
      name: 'Number of people',
      type: FieldType.Count,
      isOutput: true,
    })
    this.updater.addWatchEffect('outputs', () => {
      let ctx = CalcContext.create();
      this.proj.toplevelData.calcOutputs(ctx);
      ctx.res = {};
      let res = this.calcOutputs(ctx);
      this.outputSensibleLoad.value = res.total.cooling.sensible;
      this.outputLatentLoad.value = res.total.cooling.latent;
      this.outputNumPeople.value = res.numPeople;
    })
    
    this.internalGainsHelpInfo = {
      helpText: `Internal gains are calculated differently depending on whether you use the standard method or manual entry:

When using the standard method:
- Occupancy is estimated as (Number of Bedrooms + 1)
- Sensible load from occupants: 75 BTU/h per person
- Latent load from occupants: 41 BTU/h per person
- Additional sensible loads: 464 + 0.7 × Floor Area (ft²)
- Additional latent loads: 68 + 0.07 × Floor Area (ft²)

When using manual entry:
- Occupancy is directly entered
- Sensible load from occupants: 75 BTU/h per person
- Latent load from occupants: 41 BTU/h per person
- Additional sensible and latent loads are entered directly

The total sensible and latent loads are the sum of the occupant loads and additional loads.`,
    }

    this.serFields = [
      'useStdMethod',
      'additionalOccupants',
      'additionalSensibleLoads',
      'additionalLatentLoads',
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Internal Gains',
      path: 'build/internal-gains',
    }
  }

  calcOutputs(ctx) {
    ctx.startSection("Internals")
    ctx.isManual = this.useStdMethod.value == YesNo.No;
    if (ctx.isManual) {
      ctx.N_occ = this.additionalOccupants.value;
      ctx.q_occ_s = ctx.eval('75*N_occ', {}, 'q_occ_s');
      ctx.q_occ_l = ctx.eval('41*N_occ', {}, 'q_occ_l');
      ctx.q_I_s = this.additionalSensibleLoads.value;
      ctx.q_I_l = this.additionalLatentLoads.value;
    } else {
      ctx.N_occ = ctx.eval('N_br + 1', {N_br: ctx.toplevelData.numberBedrooms}, 'N_occ');
      ctx.A_floor = ctx.toplevelData.totalFloorArea;
      ctx.q_occ_s = ctx.eval('75*N_occ', {}, 'q_occ_s');
      ctx.q_occ_l = ctx.eval('41*N_occ', {}, 'q_occ_l');
      ctx.q_I_s = ctx.eval('464 + 0.7*A_floor', {}, 'q_I_s');
      ctx.q_I_l = ctx.eval('68 + 0.07*A_floor', {}, 'q_I_l');
    }
    ctx.q_I_s_tot = ctx.eval('q_occ_s + q_I_s', {}, 'q_I_s_tot');
    ctx.q_I_l_tot = ctx.eval('q_occ_l + q_I_l', {}, 'q_I_l_tot');

    let internalsRes = {
      numPeople: ctx.N_occ,
      people: {
        cooling: {sensible: ctx.q_occ_s, latent: ctx.q_occ_l},
        heating: {},
      },
      other: {
        cooling: {sensible: ctx.q_I_s, latent: ctx.q_I_l},
        heating: {},
      },
      total: {
        cooling: {sensible: ctx.q_I_s_tot, latent: ctx.q_I_l_tot},
        heating: {},
      },
    }
    ctx.res.internals = internalsRes;
    ctx.endSection();

    return internalsRes;
  }
}
setupClass(HouseInternalsData);
